/**
 * Elenco utenti con sospensione, reset password e consumo di risorse (F-063).
 *
 * Tre scelte che vale la pena giustificare:
 *
 * 1. **La sospensione dichiara ciò che non fa.** Il modal dice esplicitamente che
 *    una sessione già aperta resta valida fino a quindici minuti: è vero, non è
 *    ovvio, e chi sospende un account per un incidente deve saperlo *prima* di
 *    contarci sopra.
 * 2. **La password generata si vede una volta sola.** Non viene rimessa in cache,
 *    non ricompare ricaricando: se l'amministratore chiude il riquadro senza
 *    copiarla, deve rifare il reset. È l'unico comportamento che non lascia una
 *    password in chiaro in giro per lo stato dell'applicazione.
 * 3. **Un amministratore non si può sospendere.** Il pulsante non c'è per il
 *    proprio account: il backend lo rifiuta comunque, ma offrire un'azione che
 *    verrà negata è un modo di far perdere tempo.
 */

import { Fragment, useState } from 'react';
import { ApiError } from '@/lib/api-client';
import { formatDateTime, formatNumber } from '@/lib/format';
import { Alert, Badge, Button, Card, Field, Modal, Select, Spinner, Switch, TableWrap } from '@/ui';
import { useAuth } from '@/features/auth/auth-context';
import {
  useAdminUsers,
  useCreateAdminUser,
  useResetDailyQuotas,
  useResetUserPassword,
  useUpdateAdminUser,
  type AdminUser,
  type UserStatusFilter,
} from './api';

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : fallback;
}

/**
 * Un `{' '}` fra badge dà un solo carattere di spazio, che con il padding del
 * badge sparisce visivamente: la riga «Attivo Admin Premium» si legge come una
 * parola sola. Un contenitore con `gap` è la differenza fra leggibile e no.
 */
function StatusBadges({ user }: { user: AdminUser }) {
  return (
    <div className="admin__badges">
      {user.disabledAt ? <Badge tone="negative">Sospeso</Badge> : <Badge tone="positive">Attivo</Badge>}
      {user.isAdmin && <Badge tone="accent">Admin</Badge>}
      {user.isPremium && <Badge>Premium</Badge>}
    </div>
  );
}

/**
 * Contatori giornalieri consumabili dall'utente, con l'azione per azzerarli.
 *
 * **Nessun modal di conferma**, a differenza di sospensione e reset password:
 * quelle due hanno conseguenze dure da disfare (accesso bloccato, sessioni
 * chiuse), questa no — nel peggiore dei casi restituisce a un utente un budget
 * giornaliero un po' prima del previsto. Il rischio non giustifica l'attrito
 * di un secondo click di conferma.
 *
 * Il pulsante non compare affatto se **nessun** contatore ha qualcosa da
 * azzerare (`used` a zero su entrambi): offrirlo comunque inviterebbe a
 * cliccarlo "per sicurezza" su un account che non ne ha bisogno.
 */
function DailyQuotasPanel({ user }: { user: AdminUser }) {
  const reset = useResetDailyQuotas();
  const { analysis, discovery } = user.dailyQuotas;
  const hasSomethingToReset = analysis.used > 0 || (discovery?.used ?? 0) > 0;

  return (
    <div className="admin__quotas">
      <div className="admin__quota">
        <span className="admin__quota-label">Analisi titoli (oggi)</span>
        <span className="admin__quota-value">
          {formatNumber(analysis.used)} / {formatNumber(analysis.limit)}
        </span>
      </div>
      {discovery && (
        <div className="admin__quota">
          <span className="admin__quota-label">Scoperta «Scopri» (oggi)</span>
          <span className="admin__quota-value">
            {formatNumber(discovery.used)} / {formatNumber(discovery.limit)}
          </span>
        </div>
      )}
      {hasSomethingToReset && (
        <Button variant="ghost" loading={reset.isPending} onClick={() => reset.mutate(user.id)}>
          Azzera contatori di oggi
        </Button>
      )}
      {reset.error !== null && (
        <span className="admin__quota-error">{errorMessage(reset.error, 'Azzeramento non riuscito')}</span>
      )}
      {reset.isSuccess && <span className="admin__muted">Azzerati.</span>}
    </div>
  );
}

/**
 * Le undici grandezze non stanno in una riga di tabella senza renderla
 * illeggibile: in colonna vanno le cinque che dicono se un account è vivo, il
 * resto sta nel dettaglio espandibile.
 */
function ResourceSummary({ user }: { user: AdminUser }) {
  const r = user.resources;
  return (
    <dl className="admin__resources">
      <div>
        <dt>Portafogli</dt>
        <dd>{formatNumber(r.portfolios)}</dd>
      </div>
      <div>
        <dt>Transazioni</dt>
        <dd>{formatNumber(r.transactions)}</dd>
      </div>
      <div>
        <dt>Import</dt>
        <dd>{formatNumber(r.imports)}</dd>
      </div>
      <div>
        <dt>Watchlist</dt>
        <dd>{formatNumber(r.watchlistItems)}</dd>
      </div>
      <div>
        <dt>Wishlist</dt>
        <dd>{formatNumber(r.wishlistItems)}</dd>
      </div>
      <div>
        <dt>Analisi AI</dt>
        <dd>{formatNumber(r.analyses)}</dd>
      </div>
      <div>
        <dt>Briefing portafoglio</dt>
        <dd>{formatNumber(r.portfolioBriefings)}</dd>
      </div>
      <div>
        <dt>Briefing mercato</dt>
        <dd>{formatNumber(r.marketBriefings)}</dd>
      </div>
      <div>
        <dt>Connessioni broker</dt>
        <dd>{formatNumber(r.brokerConnections)}</dd>
      </div>
      <div>
        <dt>Proposte auto-trading</dt>
        <dd>{formatNumber(r.autoTradeProposals)}</dd>
      </div>
      <div>
        <dt>Sessioni attive</dt>
        <dd>{formatNumber(r.activeSessions)}</dd>
      </div>
    </dl>
  );
}

type DisableTarget = { user: AdminUser; disable: boolean };

function DisableModal({
  target,
  onClose,
  onConfirm,
  pending,
  error,
}: {
  target: DisableTarget;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  pending: boolean;
  error: unknown;
}) {
  const [reason, setReason] = useState('');
  const { user, disable } = target;

  return (
    <Modal title={disable ? 'Sospendere questo account?' : 'Riabilitare questo account?'} onClose={onClose}>
      <p>
        <strong>{user.displayName ?? user.email}</strong> — {user.email}
      </p>

      {disable ? (
        <>
          <p>
            L'utente non potrà più accedere e tutte le sue sessioni verranno chiuse. I dati (portafogli,
            transazioni, storico di consumo) <strong>restano</strong>: sospendere non cancella.
          </p>
          <Alert variant="warning" title="Una sessione già aperta resta valida fino a 15 minuti">
            Il token di accesso è a vita breve e non viene revocato uno per uno. La sospensione blocca
            subito login e rinnovo, quindi l'accesso si chiude al più tardi allo scadere del token in
            corso.
          </Alert>
          <Field
            label="Motivo (facoltativo)"
            value={reason}
            maxLength={200}
            onChange={(event) => setReason(event.target.value)}
            hint="Viene mostrato all'utente al tentativo di accesso."
          />
        </>
      ) : (
        <p>L'utente potrà accedere di nuovo. Dovrà effettuare un nuovo login: le vecchie sessioni sono state chiuse.</p>
      )}

      {error !== null && error !== undefined && (
        <Alert variant="error" title="Operazione non riuscita">
          {errorMessage(error, 'Errore imprevisto')}
        </Alert>
      )}

      <div className="admin__modal-actions">
        <Button variant="ghost" onClick={onClose}>
          Annulla
        </Button>
        <Button
          variant={disable ? 'danger' : 'primary'}
          loading={pending}
          onClick={() => onConfirm(reason)}
        >
          {disable ? 'Sospendi' : 'Riabilita'}
        </Button>
      </div>
    </Modal>
  );
}

function ResetPasswordModal({
  user,
  onClose,
}: {
  user: AdminUser;
  onClose: () => void;
}) {
  const reset = useResetUserPassword();
  const [custom, setCustom] = useState('');
  const [useCustom, setUseCustom] = useState(false);

  const generated = reset.data?.temporaryPassword ?? null;

  if (reset.isSuccess) {
    return (
      <Modal title="Password reimpostata" onClose={onClose}>
        <p>
          Tutte le sessioni di <strong>{user.email}</strong> sono state chiuse (
          {formatNumber(reset.data.revokedSessions)}).
        </p>
        {generated ? (
          <>
            <Alert variant="warning" title="Questa password non sarà più mostrata">
              Copiala adesso e consegnala all'utente su un canale sicuro. Non è recuperabile: se la
              perdi, l'unica cosa da fare è ripetere il reset.
            </Alert>
            <p className="admin__secret">
              <code>{generated}</code>
            </p>
          </>
        ) : (
          <p>La password scelta è attiva. Comunicala all'utente su un canale sicuro.</p>
        )}
        <div className="admin__modal-actions">
          <Button onClick={onClose}>Chiudi</Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Reimpostare la password?" onClose={onClose}>
      <p>
        <strong>{user.displayName ?? user.email}</strong> — {user.email}
      </p>
      <p>
        Tutte le sessioni aperte verranno chiuse. La password precedente smette di funzionare
        immediatamente.
      </p>

      <label className="admin__radio">
        <input type="radio" checked={!useCustom} onChange={() => setUseCustom(false)} />
        <span>
          <strong>Genera una password</strong> — 192 bit di casualità, mostrata una volta sola.
          Preferibile: una password scelta per conto di qualcun altro è quasi sempre debole.
        </span>
      </label>
      <label className="admin__radio">
        <input type="radio" checked={useCustom} onChange={() => setUseCustom(true)} />
        <span>
          <strong>Scegline una</strong> — almeno 12 caratteri.
        </span>
      </label>

      {useCustom && (
        <Field
          label="Nuova password"
          type="password"
          value={custom}
          autoComplete="new-password"
          onChange={(event) => setCustom(event.target.value)}
        />
      )}

      {reset.error !== null && (
        <Alert variant="error" title="Reset non riuscito">
          {errorMessage(reset.error, 'Errore imprevisto')}
        </Alert>
      )}

      <div className="admin__modal-actions">
        <Button variant="ghost" onClick={onClose}>
          Annulla
        </Button>
        <Button
          variant="danger"
          loading={reset.isPending}
          disabled={useCustom && custom.length < 12}
          onClick={() =>
            reset.mutate(useCustom ? { userId: user.id, newPassword: custom } : { userId: user.id })
          }
        >
          Reimposta
        </Button>
      </div>
    </Modal>
  );
}

/**
 * Una riga, estratta in un componente proprio e non inline dentro `.map()`:
 * `DailyQuotasPanel` deve chiamare `useResetDailyQuotas()`, e un hook non può
 * vivere dentro il corpo di un callback in un ciclo — solo dentro un
 * componente. Come effetto collaterale la riga espansa (dettaglio + contatori)
 * ora ha il proprio stato di mutazione isolato dalle altre righe.
 */
/**
 * Promozione e retrocessione dal piano premium (F-088).
 *
 * L'endpoint c'era già (`PATCH /admin/users/:id` accetta `isPremium`, con i
 * suoi test): mancava solo il modo di raggiungerlo su un utente **esistente** —
 * il piano si poteva scegliere solo al momento della creazione, e dopo restava
 * un badge che non si poteva né mettere né togliere.
 *
 * **Nessun modal di conferma**, a differenza di sospensione e reset password:
 * il gesto è reversibile con un secondo clic e non revoca sessioni né
 * distrugge nulla. È la stessa proporzione già scelta per l'azzeramento dei
 * contatori giornalieri (F-063) — l'attrito si spende dove il danno sarebbe
 * difficile da annullare.
 */
function PremiumToggle({ user }: { user: AdminUser }) {
  const update = useUpdateAdminUser();
  const promoting = !user.isPremium;

  return (
    <Button
      variant="ghost"
      loading={update.isPending}
      title={
        promoting
          ? `Attiva il piano premium per ${user.email}`
          : `Riporta ${user.email} al piano base`
      }
      onClick={() => update.mutate({ userId: user.id, isPremium: promoting })}
    >
      {promoting ? '★ Rendi premium' : 'Togli premium'}
    </Button>
  );
}

function UserRow({
  user,
  isSelf,
  open,
  onToggle,
  onResetPassword,
  onDisable,
}: {
  user: AdminUser;
  isSelf: boolean;
  open: boolean;
  onToggle: () => void;
  onResetPassword: () => void;
  onDisable: (disable: boolean) => void;
}) {
  return (
    <Fragment>
      <tr>
        <td>
          <button type="button" className="admin__link" aria-expanded={open} onClick={onToggle}>
            {user.displayName ?? user.email}
          </button>
          <div className="admin__muted">{user.email}</div>
        </td>
        <td>
          <StatusBadges user={user} />
          {user.disabledReason && <div className="admin__muted">{user.disabledReason}</div>}
        </td>
        <td className="numeric">{formatNumber(user.resources.portfolios)}</td>
        <td className="numeric">{formatNumber(user.resources.transactions)}</td>
        <td className="numeric">{formatNumber(user.resources.analyses)}</td>
        <td className="numeric">
          {formatNumber(user.llm.totalTokens)}
          <div className="admin__muted">
            {formatNumber(user.llm.calls)} chiamate
            {user.llm.callsWithoutUsage > 0 && ` · ${formatNumber(user.llm.callsWithoutUsage)} senza conteggi`}
          </div>
        </td>
        <td>{user.lastLoginAt ? formatDateTime(user.lastLoginAt) : '—'}</td>
        <td>
          <div className="admin__row-actions">
            <PremiumToggle user={user} />
            <Button variant="ghost" onClick={onResetPassword}>
              Reset password
            </Button>
            {user.disabledAt ? (
              <Button variant="secondary" onClick={() => onDisable(false)}>
                Riabilita
              </Button>
            ) : (
              <Button
                variant="ghost"
                disabled={isSelf}
                title={isSelf ? 'Non puoi sospendere il tuo stesso account' : undefined}
                onClick={() => onDisable(true)}
              >
                Sospendi
              </Button>
            )}
          </div>
        </td>
      </tr>
      {open && (
        <tr className="admin__detail-row">
          <td colSpan={8}>
            <ResourceSummary user={user} />
            <DailyQuotasPanel user={user} />
            <p className="admin__muted">
              Consumo LLM complessivo: {formatNumber(user.llm.lifetimeTokens)} token in{' '}
              {formatNumber(user.llm.lifetimeCalls)} chiamate · account creato il{' '}
              {formatDateTime(user.createdAt)}
            </p>
          </td>
        </tr>
      )}
    </Fragment>
  );
}

/**
 * Creazione di un utente dal pannello (F-082).
 *
 * Funziona anche a registrazioni pubbliche chiuse, ed e' il motivo per cui
 * esiste: `ALLOW_REGISTRATION` chiude la porta a chi arriva da fuori, non a chi
 * ha gia' il ruolo di amministratore.
 *
 * La password generata si mostra **una volta sola**, con lo stesso avviso della
 * reimpostazione: non e' recuperabile, e scoprirlo dopo aver chiuso il modal
 * significa dover rifare tutto.
 */
function CreateUserModal({ onClose }: { onClose: () => void }) {
  const create = useCreateAdminUser();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [custom, setCustom] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [isPremium, setIsPremium] = useState(false);

  if (create.isSuccess) {
    const generated = create.data.temporaryPassword;
    return (
      <Modal title="Utente creato" onClose={onClose}>
        <p>
          <strong>{create.data.user.email}</strong> puo' ora accedere.
        </p>
        {generated ? (
          <>
            <Alert variant="warning" title="Questa password non sara' piu' mostrata">
              Copiala adesso e consegnala all'utente su un canale sicuro. Non e' recuperabile: se la
              perdi, l'unica cosa da fare e' reimpostarla.
            </Alert>
            <p className="admin__secret">
              <code>{generated}</code>
            </p>
          </>
        ) : (
          <p>La password scelta e' attiva. Comunicala all'utente su un canale sicuro.</p>
        )}
        <div className="admin__modal-actions">
          <Button onClick={onClose}>Chiudi</Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Nuovo utente" onClose={onClose}>
      <Field
        label="Email"
        type="email"
        value={email}
        autoComplete="off"
        onChange={(event) => setEmail(event.target.value)}
      />
      <Field
        label="Nome visualizzato (facoltativo)"
        value={displayName}
        onChange={(event) => setDisplayName(event.target.value)}
      />

      <label className="admin__radio">
        <input type="radio" checked={!useCustom} onChange={() => setUseCustom(false)} />
        <span>
          <strong>Genera una password</strong> — mostrata una volta sola. Preferibile: una password
          scelta per conto di qualcun altro e' quasi sempre debole.
        </span>
      </label>
      <label className="admin__radio">
        <input type="radio" checked={useCustom} onChange={() => setUseCustom(true)} />
        <span>
          <strong>Scegline una</strong> — almeno 12 caratteri.
        </span>
      </label>

      {useCustom && (
        <Field
          label="Password"
          type="password"
          value={custom}
          autoComplete="new-password"
          onChange={(event) => setCustom(event.target.value)}
        />
      )}

      <Switch label="Piano premium" checked={isPremium} onChange={setIsPremium} />

      {create.error !== null && (
        <Alert variant="error" title="Utente non creato">
          {errorMessage(create.error, 'Creazione non riuscita')}
        </Alert>
      )}

      <div className="admin__modal-actions">
        <Button variant="secondary" onClick={onClose}>
          Annulla
        </Button>
        <Button
          loading={create.isPending}
          disabled={!email.trim()}
          onClick={() =>
            create.mutate({
              email: email.trim(),
              isPremium,
              ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
              ...(useCustom && custom ? { password: custom } : {}),
            })
          }
        >
          Crea
        </Button>
      </div>
    </Modal>
  );
}

export function AdminUsersPage() {
  const { user: currentUser } = useAuth();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<UserStatusFilter>('all');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [disableTarget, setDisableTarget] = useState<DisableTarget | null>(null);
  const [resetTarget, setResetTarget] = useState<AdminUser | null>(null);
  const [creating, setCreating] = useState(false);

  const users = useAdminUsers(search, status);
  const update = useUpdateAdminUser();

  const confirmDisable = (reason: string) => {
    if (!disableTarget) return;
    update.mutate(
      {
        userId: disableTarget.user.id,
        disabled: disableTarget.disable,
        ...(disableTarget.disable && reason.trim() ? { disabledReason: reason.trim() } : {}),
      },
      { onSuccess: () => setDisableTarget(null) },
    );
  };

  return (
    <>
      <Card
        title="Utenti"
        subtitle={
          users.data
            ? `${formatNumber(users.data.total)} in elenco · consumo LLM sugli ultimi ${users.data.usageDays} giorni`
            : undefined
        }
        action={<Button onClick={() => setCreating(true)}>Nuovo utente</Button>}
      >
        <div className="admin__filters">
          <Field
            label="Cerca"
            value={search}
            placeholder="email o nome"
            onChange={(event) => setSearch(event.target.value)}
          />
          <Select
            label="Stato"
            value={status}
            onChange={(event) => setStatus(event.target.value as UserStatusFilter)}
            options={[
              { value: 'all', label: 'Tutti' },
              { value: 'active', label: 'Attivi' },
              { value: 'disabled', label: 'Sospesi' },
            ]}
          />
        </div>
      </Card>

      {users.isPending ? (
        <Spinner />
      ) : users.error ? (
        <Alert variant="error" title="Elenco non caricato">
          {errorMessage(users.error, 'Errore imprevisto')}
        </Alert>
      ) : users.data.items.length === 0 ? (
        <Card>
          <p>Nessun utente corrisponde ai filtri.</p>
        </Card>
      ) : (
        <Card flush>
          <TableWrap>
            <table className="table table--compact">
              <thead>
                <tr>
                  <th>Utente</th>
                  <th>Stato</th>
                  <th className="numeric">Portafogli</th>
                  <th className="numeric">Transazioni</th>
                  <th className="numeric">Analisi AI</th>
                  <th className="numeric">Token LLM</th>
                  <th>Ultimo accesso</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {users.data.items.map((user) => (
                  <UserRow
                    key={user.id}
                    user={user}
                    isSelf={user.id === currentUser?.id}
                    open={expanded === user.id}
                    onToggle={() => setExpanded(expanded === user.id ? null : user.id)}
                    onResetPassword={() => setResetTarget(user)}
                    onDisable={(disable) => setDisableTarget({ user, disable })}
                  />
                ))}
              </tbody>
            </table>
          </TableWrap>
        </Card>
      )}

      {disableTarget && (
        <DisableModal
          target={disableTarget}
          pending={update.isPending}
          error={update.error}
          onClose={() => setDisableTarget(null)}
          onConfirm={confirmDisable}
        />
      )}

      {resetTarget && <ResetPasswordModal user={resetTarget} onClose={() => setResetTarget(null)} />}
      {creating && <CreateUserModal onClose={() => setCreating(false)} />}
    </>
  );
}
